import React from 'react';
import { Box } from '@mui/material';

// Shared Components
import SolutionHero from '../components/SolutionHero';
import ServicesGrid from '../components/ServicesGrid';
import ProcessTimeline from '../components/ProcessTimeline';
import BenefitsSection from '../components/BenefitsSection';
import FAQAccordion from '../components/FAQAccordion';
import SolutionCTA from '../components/SolutionCTA';

// Icons
import DataObjectIcon from '@mui/icons-material/DataObject';
import CloudIcon from '@mui/icons-material/Cloud';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import StreamIcon from '@mui/icons-material/Stream';
import FactCheckIcon from '@mui/icons-material/FactCheck';

const DataEngineering = () => {
    // Page Content
    const services = [
        {
            icon: <DataObjectIcon sx={{ fontSize: 28 }} />,
            title: "Data Pipeline Architecture",
            description: "Design and build robust ETL/ELT pipelines that transform raw data into AI-ready datasets at scale."
        },
        {
            icon: <CloudIcon sx={{ fontSize: 28 }} />,
            title: "Cloud Migration & Optimization",
            description: "Migrate, modernize, and optimize your data infrastructure on AWS, GCP, or Azure with cost-efficient architectures."
        },
        {
            icon: <SettingsSuggestIcon sx={{ fontSize: 28 }} />,
            title: "MLOps & Model Serving",
            description: "Build end-to-end ML lifecycle infrastructure: versioning, training pipelines, serving, and monitoring."
        },
        {
            icon: <WarehouseIcon sx={{ fontSize: 28 }} />,
            title: "Data Lake & Warehouse Design",
            description: "Architect modern data platforms using Snowflake, Databricks, BigQuery, or Redshift for analytics and AI."
        },
        {
            icon: <StreamIcon sx={{ fontSize: 28 }} />,
            title: "Real-Time Streaming",
            description: "Implement Kafka, Kinesis, or Pub/Sub streaming architectures for real-time analytics and event processing."
        },
        {
            icon: <FactCheckIcon sx={{ fontSize: 28 }} />,
            title: "Data Quality & Governance",
            description: "Establish data cataloging, lineage tracking, quality monitoring, and governance frameworks."
        }
    ];

    const processSteps = [
        {
            title: "Infrastructure Audit",
            description: "Assess current data landscape, identify gaps, and document pain points and requirements."
        },
        {
            title: "Architecture Design",
            description: "Design target-state architecture with detailed technical specifications and migration plans."
        },
        {
            title: "Implementation",
            description: "Build pipelines, migrate data, implement governance, and deploy infrastructure as code."
        },
        {
            title: "Optimization",
            description: "Tune performance, optimize costs, and establish operational playbooks for ongoing management."
        }
    ];

    const benefits = [
        {
            title: "AI-Ready Foundation",
            description: "Data infrastructure designed from the ground up to support ML training, inference, and analytics workloads."
        },
        {
            title: "Scalability by Design",
            description: "Architectures that grow with your data volumes—from gigabytes to petabytes without rearchitecting."
        },
        {
            title: "Cost Optimization",
            description: "Right-sized compute, smart tiering, and automated scaling to minimize cloud spend without sacrificing performance."
        },
        {
            title: "Operational Excellence",
            description: "Infrastructure as code, automated monitoring, self-healing systems, and documented runbooks for reliability."
        }
    ];

    const faqs = [
        {
            question: "Which cloud platforms do you work with?",
            answer: "We work across all major clouds: AWS (S3, Redshift, Glue, SageMaker), GCP (BigQuery, Dataflow, Vertex AI), and Azure (Synapse, Data Factory, ML). We're also experienced with multi-cloud and hybrid architectures."
        },
        {
            question: "Can you work with our existing data stack?",
            answer: "Absolutely. We integrate with and extend existing investments—whether that's legacy databases, Hadoop clusters, or modern data platforms. We modernize incrementally rather than requiring full replacements."
        },
        {
            question: "How do you handle data security?",
            answer: "Security is built-in at every layer: encryption at rest and in transit, IAM policies, network isolation, audit logging, and compliance with GDPR, HIPAA, SOC2, and industry-specific regulations."
        },
        {
            question: "What's the typical timeline for a data platform project?",
            answer: "Initial infrastructure and core pipelines typically take 4-8 weeks. Full platform buildout with advanced features, governance, and MLOps can take 3-6 months depending on complexity."
        },
        {
            question: "Do you provide ongoing support?",
            answer: "Yes. We offer managed services for ongoing operations, monitoring, optimization, and platform evolution. Alternatively, we can train your team for full handoff."
        }
    ];

    const ctaStats = [
        { value: "10PB+", label: "Data Migrated" },
        { value: "50%", label: "Cost Reduction" },
        { value: "99.9%", label: "Uptime SLA" },
        { value: "5x", label: "Query Performance" }
    ];

    return (
        <Box sx={{ bgcolor: '#030014', minHeight: '100vh' }}>
            {/* Hero Section */}
            <SolutionHero
                headline="AI-Ready"
                highlightedText="Data Infrastructure"
                subheadline="Modern data pipelines, cloud architecture, and MLOps that power your AI ambitions. Build the foundation that makes machine learning production-ready."
                primaryCTA="Assess Your Stack"
                primaryCTALink="/contact-us"
                secondaryCTA="View Case Studies"
                secondaryCTALink="/case-studies"
            />

            {/* Services Grid */}
            <ServicesGrid
                title="Data & Cloud Services"
                subtitle="Infrastructure Solutions"
                services={services}
            />

            {/* Process Timeline */}
            <ProcessTimeline
                title="Our Implementation Approach"
                subtitle="How We Deliver"
                steps={processSteps}
            />

            {/* Benefits Section */}
            <BenefitsSection
                title="The Data Foundation Advantage"
                subtitle="Why It Matters"
                benefits={benefits}
            />

            {/* FAQ Section */}
            <FAQAccordion
                title="Frequently Asked Questions"
                subtitle="Data Engineering FAQ"
                faqs={faqs}
            />

            {/* CTA Section */}
            <SolutionCTA
                headline="Ready to Build Your AI-Ready Data Platform?"
                subheadline="Great AI starts with great data infrastructure. Let's build the foundation that powers your intelligent enterprise."
                primaryCTA="Start a Conversation"
                primaryCTALink="/contact-us"
                secondaryCTA="Explore Our Work"
                secondaryCTALink="/case-studies"
                stats={ctaStats}
            />
        </Box>
    );
};

export default DataEngineering;
